import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const veedVideoBackgroundRemovalParams = z
  .object({
    subjectIsPerson: z.boolean().default(true),
    attachments: z.array(attachment).optional(),
  })
  .strip();

export const veedVideoBackgroundRemoval = defineModel({
  key: "veed-video-background-removal",
  stepType: "video",
  spec: {
    label: "Remove BG - Standard",
    description:
      "Remove backgrounds from videos with high quality edge refinement",
    icon: "bg-remove.svg",
    tags: ["video-edit"],
    featured: true,
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: {
      attachments: true,
      subjectIsPerson: true,
    } as const,
    options: {
      attachments: [
        {
          type: "video",
          allowedFormats: ["video/mp4", "video/webm", "video/quicktime"],
          label: "Video",
          slot: "video",
          required: true,
        },
      ],
      subjectIsPerson: {
        items: [
          { label: "Person", value: "true" },
          { label: "Object", value: "false" },
        ],
      },
    },
    params: veedVideoBackgroundRemovalParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/veed/video-background-removal",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 22_500 }],
  },
  providers: [
    {
      provider: "fal",
      modelId: "veed/video-background-removal",
      estimateCost(_params) {
        // VEED pricing: $0.0225 per 30 frames with refine ON
        // Assuming 30fps, that's $0.0225 per second
        // For a 10s video estimate: $0.225
        return 0.0225 * 10 * 1_000_000 * (1 + BRAND_MARKUP);
      },
      // $0.0225 per 30 frames = $0.0225 per second at 30fps
      costPerSecond: 0.0225 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const getUrl = (slot: string) =>
          params.attachments?.find((attachment) => attachment.slot === slot)
            ?.data;

        const videoUrl = getUrl("video");

        if (!videoUrl) {
          throw new Error(
            "Video URL is required for VEED video background removal",
          );
        }

        return {
          model: this.modelId,
          prompt: "", // VEED doesn't use prompts, but FalVideoPayload requires it
          video_url: videoUrl,
          // Use vp9 for alpha channel support (transparent background)
          output_codec: "vp9",
          refine_foreground_edges: true,
          subject_is_person: params.subjectIsPerson,
        };
      },
    },
  ],
});
